import { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Receipt, X, BellOff, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// December-only banner that surfaces the user's actual harvestable
// losses on the Portfolio page. Renders only if:
//   1. Today is between Dec 1 and Dec 31 inclusive.
//   2. The user hasn't permanently dismissed it (localStorage flag).
//   3. There IS something to harvest — total LT + ST loss > 0.
//
// Dismiss options offered:
//   • "Dismiss for this visit" — closes the card; reappears next page
//     load (so a user who lands on the Portfolio page tomorrow sees
//     the reminder again, as the user explicitly requested).
//   • "Don't show me this again this year" — sets a localStorage flag
//     keyed to the year so it stays hidden through Dec 31 of THIS
//     year. Reappears Dec 1 of next year.
//
// Year-keyed dismiss is intentional: someone who hits "don't remind"
// in 2026 should still get the 2027 nudge — the tax math resets every
// calendar year and the harvestable amount is different.

const STORAGE_KEY_PREFIX = 'tlh_banner_dismissed_';

function isDecemberWindow() {
  const now = new Date();
  return now.getMonth() === 11; // 0-indexed: 11 = December
}

function currentYearDismissKey() {
  return `${STORAGE_KEY_PREFIX}${new Date().getFullYear()}`;
}

export default function TaxHarvestBanner() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sessionDismissed, setSessionDismissed] = useState(false);
  const [permanentlyDismissed, setPermanentlyDismissed] = useState(false);

  useEffect(() => {
    if (!isDecemberWindow()) {
      setLoading(false);
      return;
    }
    try {
      if (localStorage.getItem(currentYearDismissKey()) === '1') {
        setPermanentlyDismissed(true);
        setLoading(false);
        return;
      }
    } catch { /* private mode — ignore */ }

    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/portfolio/tax-lots`, { withCredentials: true });
        if (!cancelled) setData(data);
      } catch {
        // silent — banner just won't render
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || !isDecemberWindow() || permanentlyDismissed || sessionDismissed) return null;
  if (!data?.summary) return null;
  const { lt_loss_available = 0, st_loss_available = 0 } = data.summary;
  const total = lt_loss_available + st_loss_available;
  if (total <= 0) return null;

  const dismissPermanently = () => {
    try { localStorage.setItem(currentYearDismissKey(), '1'); } catch { /* ignore */ }
    setPermanentlyDismissed(true);
  };

  return (
    <div
      className="rounded-xl border-2 border-amber-300/70 dark:border-amber-700/70 bg-gradient-to-r from-amber-50/80 to-rose-50/40 dark:from-amber-950/40 dark:to-rose-950/30 p-4 mb-4 shadow-sm"
      data-testid="tax-harvest-banner"
    >
      <div className="flex items-start gap-3">
        <div className="hidden sm:flex w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-950 items-center justify-center shrink-0">
          <Receipt className="w-5 h-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center justify-between gap-2 mb-1.5">
            <div className="text-xs sm:text-sm font-bold text-amber-800 dark:text-amber-200 uppercase tracking-wider">
              Year-End Tax-Loss Harvest Reminder
            </div>
            <button
              type="button"
              onClick={() => setSessionDismissed(true)}
              className="text-muted-foreground hover:text-foreground -mt-1 -mr-1 p-1"
              title="Hide for this visit (will reappear next time you load the Portfolio page)"
              aria-label="Dismiss for this visit"
              data-testid="tax-harvest-banner-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          <p className="text-sm text-foreground leading-relaxed">
            You currently have{' '}
            {lt_loss_available > 0 && (
              <>
                <span className="font-mono font-bold text-rose-700 dark:text-rose-300">${lt_loss_available.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>{' '}
                in long-term losses
              </>
            )}
            {lt_loss_available > 0 && st_loss_available > 0 && ' and '}
            {st_loss_available > 0 && (
              <>
                <span className="font-mono font-bold text-rose-700 dark:text-rose-300">${st_loss_available.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>{' '}
                in short-term losses
              </>
            )}
            {' '}available to harvest before December 31. The realized loss can offset capital gains and (US federal) up to $3,000 of ordinary income — review which lots to sell and check for wash-sale risks.
          </p>
          <div className="flex flex-wrap items-center gap-2 mt-3">
            <Link
              to="/tax-lots"
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg bg-amber-600 hover:bg-amber-700 text-white text-sm font-semibold transition-colors"
              data-testid="tax-harvest-banner-cta"
            >
              Open Tax Lot Optimizer
              <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <button
              type="button"
              onClick={dismissPermanently}
              className="inline-flex items-center gap-1.5 px-3 h-9 rounded-lg border border-border bg-card hover:bg-muted text-xs font-semibold text-muted-foreground transition-colors"
              title={`Don't show this banner again until December ${new Date().getFullYear() + 1}.`}
              data-testid="tax-harvest-banner-mute"
            >
              <BellOff className="w-3.5 h-3.5" />
              Don\'t remind me this year
            </button>
            <span className="text-[11px] text-muted-foreground">
              Or close it (top-right ✕) to keep the nudge each visit.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
